
/**
 * @typedef {Object} ThresholdSet
 * @property {number|null} val - Valor del umbral
 * @property {boolean} mod - Flag de modificación (false por ahora, futuro)
 * @property {boolean} ctl - Flag de control (true si uso === "CTL")
 */

/**
 * @typedef {Object} ParameterThresholdSet
 * @property {number} idPar - Código de parámetro
 * @property {ThresholdSet} upper - Umbral superior
 * @property {ThresholdSet} lower - Umbral inferior
 */

/**
 * Mapeo de idPar a índice en ptsa
 * @type {Object<number, number>}
 */
/* const PARAMETER_ID_TO_INDEX = {
  1: 0,
  6: 1,
  5: 2,
  4: 3
}; */

/**
 * Orden esperado de parámetros (para inicialización)
 * @type {number[]}
 */
const PARAMETER_ORDER = [1, 6, 5, 4];

/**
 * Busca un setpoint por idPar en el arreglo de setpoints
 * @param {Array} setpointsArray - Arreglo de setpoints desde $store
 * @param {number} idPar - ID del parámetro a buscar
 * @returns {Object|null} El setpoint encontrado o null
 */
function findSetpointByIdPar(setpointsArray, idPar) {
  if (!setpointsArray || !Array.isArray(setpointsArray)) {
    return null;
  }
  return setpointsArray.find(sp => sp.idPar === idPar) || null;
}

/**
 * Determina si los umbrales están habilitados para control
 * @param {string} uso - Campo "uso" del setpoint ("CTL" o "REF")
 * @returns {boolean} true si uso === "CTL", false en caso contrario
 */
function isControlEnabled(uso) {
  return uso === 'CTL';
}

/**
 * Extrae y transforma datos de setpoints en estructura de umbrales
 * @param {Array} setpointsArray - Arreglo de setpoints desde $store.state.login.userLogin.info.default_setpoints
 * @returns {ParameterThresholdSet[]} Array con estructura de umbrales en orden [idPar 1, 6, 5, 4]
 */
export function getParameterThresholdSetArray(setpointsArray) {
  const ptsa = PARAMETER_ORDER.map(idPar => {
    const setpoint = findSetpointByIdPar(setpointsArray, idPar);
    const isControl = setpoint ? isControlEnabled(setpoint.uso) : false;
    
    // Si no encontramos el setpoint, valores nulos
    if (!setpoint) {
      return {
        idPar,
        upper: { val: null, mod: false, ctl: false },
        lower: { val: null, mod: false, ctl: false }
      };
    }
    
    // Asignar valores directos de umbrales
    return {
      idPar,
      upper: {
        val: setpoint.umbral_superior,
        mod: false,
        ctl: isControl
      },
      lower: {
        val: setpoint.umbral_inferior,
        mod: false,
        ctl: isControl
      }
    };
  });
  
  return ptsa;
}

/**
 * Versión que obtiene datos directamente desde Vue context
 * Útil si lo llamas desde mounted() en padre.vue
 * @param {Object} vueContext - this del componente Vue
 * @returns {ParameterThresholdSet[]}
 */
export function getParameterThresholdSetArrayFromStore(vueContext) {
  try {
    const setpointsArray = vueContext.$store.state.login.userLogin.info.default_setpoints;
    return getParameterThresholdSetArray(setpointsArray);
  } catch (error) {
    console.error('Error accessing $store for setpoints:', error);
    return getParameterThresholdSetArray(null); // Retorna con valores nulos
  }
}

/**
 * Utilidad para debugging: mostrar qué parámetros fueron encontrados
 * @param {Array} setpoints
 * @returns {Object} Reporte de búsqueda
 */
export function debugThresholdSearch(setpoints) {
  return {
    a_required_idPars: PARAMETER_ORDER,
    b_found_idPars: setpoints.map(sp => sp.idPar),
    c_missing_idPars: PARAMETER_ORDER.filter(id => !findSetpointByIdPar(setpoints, id)),
    d_extra_idPars: setpoints.map(sp => sp.idPar).filter(id => !PARAMETER_ORDER.includes(id))
  };
}
